import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import ViewQuotationScreen from './ViewQuotationScreen';

const QUOTES_DASHBOARD_SCREEN_ENUM = {
  DASHBOARD: 'DASHBOARD',
  QUOTATION: 'QUOTATION',
};

// Status Component
const quote_status = (val) => (
  <div
    className={`inline-flex gap-2 p-1 justify-center rounded-full items-center text-xs font-medium
      ${val === 'RFQ Sent' && 'bg-[#ebfcee] text-[#33d84c] w-32'}
      ${val === 'Quotation Received' && 'bg-[#e6f4f9] text-[#008dc1] w-40'}
      ${val === 'Quote Accepted' && 'bg-[#faebf6] text-[#c42e9c] w-32'}
    `}
  >
    <div
      className={`w-2 h-2 rounded-full
        ${val === 'RFQ Sent' && 'bg-[#33d84c]'}
        ${val === 'Quotation Recieved' && 'bg-[#008dc1]'}
        ${val === 'Quote Accepted' && 'bg-[#c42e9c]'}
      `}
    ></div>
    <div>{val}</div>
  </div>
);

// Action Buttons
const buttons = (val, setSelectedQuoteId, setField, item) => {
  const handleActionButton = (val) => {
    switch (val) {
      case 'Quotation Received':
        setSelectedQuoteId(item.quote_number);
        setField(QUOTES_DASHBOARD_SCREEN_ENUM.QUOTATION);
        break;

      default:
        break;
    }
  };

  const buttonTextMap = {
    'RFQ Sent': 'View',
    'Quotation Received': 'View',
    'Quote Accepted': 'Create Contract',
  };

  return (
    <Button
      className="bg-white border border-purple-500 text-purple-600 text-xs hover:bg-purple-50 px-3 py-1 rounded-md"
      disabled={val === 'RFQ Sent'}
      onClick={() => handleActionButton(val)}
    >
      {buttonTextMap[val] || 'Proceed'}
    </Button>
  );
};

const PartQuotesDashboardCode = ({
  setScreen,
  part,
  setSelectedQuoteId,
  setField,
}) => {
  // QUOTE Nummber sabka null h toh sabh tick hojare h
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);
  const [error, setError] = useState(null);

  var { projectId } = useParams();

  const [tableData, setTableData] = useState([]);
  const [checkedQuotes, setCheckedQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const getTableData = async () => {
      try {
        const response = await apiClient.post(
          '/api/quotation/project-part-id',
          JSON.stringify({
            project_id: Number(projectId),
            part_id: part.partId,
          }),
        );

        const responseData = response?.data;
        setTableData(responseData?.data);
      } catch (error) {
        console.error('ERROR IN GETTING SCREEN DATA');
      }
    };

    getTableData();
  }, []);

  const filteredData = tableData.filter((item) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      (item?.quote_number ?? '').toLowerCase().includes(lowerSearch) ||
      (item?.manufacturer_name ?? '').toLowerCase().includes(lowerSearch)
    );
  });

  const handleToggleAll = (isChecked) => {
    if (isChecked) {
      const newItems = filteredData.filter(
        (item) =>
          !checkedQuotes.some((i) => i.quote_number === item.quote_number),
      );
      setCheckedQuotes((prev) => [...prev, ...newItems]);
    } else {
      const remaining = checkedQuotes.filter(
        (i) =>
          !filteredData.some(
            (filteredItem) => filteredItem.quote_number === i.quote_number,
          ),
      );
      setCheckedQuotes(remaining);
    }
  };

  const handleCompareClick = () => {
    console.log('Compare clicked with:', checkedQuotes);
    // TODO: Replace this with what should happen when compare is clicked
  };

  return (
    <>
      {/* Header */}
      <div className="flex justify-between mb-6">
        <div className="flex gap-4 items-center">
          <ChevronLeft
            className="h-5 w-5 hover:cursor-pointer"
            onClick={() => setScreen('ALL_PARTS')}
          />
          <h1 className="text-lg font-medium">
            Part#{part.partId} {part.partName} - Quotations
          </h1>
        </div>

        <div className="flex gap-4 items-center">
          {/* Search Input */}
          <Input
            type="text"
            placeholder="Search Quote Number or Manufacturer"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[300px] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 disabled:border disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:text-black disabled:bg-white"
          />

          {/* Compare Button */}
          <Button
            variant="default"
            onClick={handleCompareClick}
            disabled={checkedQuotes.length < 2}
            className={`px-4 py-2 text-sm rounded-md  transition-all ease-in duration-300 ${
              checkedQuotes.length < 2
                ? 'text-gray-600 bg-gray-200 border-gray-300 cursor-not-allowed'
                : 'bg-darkPurple text-white hover:bg-darkerPurple'
            }`}
          >
            Compare
          </Button>
        </div>
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-custom9 items-center text-gray-600 text-center gap-4 py-2 bg-gray-50 rounded-md text-[14px]">
        <div className="flex justify-center">
          <Input
            type="checkbox"
            className="w-4 h-4 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
            checked={
              filteredData.length > 0 &&
              filteredData.every((item) =>
                checkedQuotes.some((i) => i.quote_number === item.quote_number),
              )
            }
            onChange={(e) => handleToggleAll(e.target.checked)}
          />
        </div>
        <div className="text-left">Quote Number</div>
        <div className="text-left">Manufacturers</div>
        <div className="text-center">Quantity</div>
        <div className="text-center">Total</div>
        <div className="text-center">Received Date</div>
        <div className="text-center">Estimated Delivery Date</div>
        <div className="text-center">Status</div>
        <div className="text-center">Action</div>
      </div>

      {/* Table Body */}
      <div className="max-h-[500px] overflow-y-auto">
        {filteredData.map((item, index) => {
          const uniqueKey = `default-${index}`;
          return (
            <div
              key={uniqueKey}
              className="grid grid-cols-custom9 gap-4 py-4 items-center text-gray-700 text-sm border-b border-gray-200 bg-white hover:bg-veryVeryLightPurple transition-all ease-in duration-300"
            >
              <div className="flex justify-center">
                <Input
                  type="checkbox"
                  className="w-4 h-4 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
                  checked={checkedQuotes.some(
                    (i) => i.quote_number === item.quote_number,
                  )}
                  onChange={(e) => {
                    setCheckedQuotes((prev) =>
                      e.target.checked
                        ? [...prev, item]
                        : prev.filter(
                            (i) => i.quote_number !== item.quote_number,
                          ),
                    );
                  }}
                />
              </div>
              <div className="text-left">{item.quote_number || '-'}</div>
              <div className="text-left">{item.manufacturer_name || '-'}</div>
              <div className="text-center">{item.quantity || '-'}</div>
              <div className="text-center">{item.total_price || '-'}</div>
              <div className="text-center">{item.received_date || '-'}</div>
              <div className="text-center">{item.deliveryDate || '-'}</div>
              <div className="text-center">{quote_status(item.status)}</div>
              <div className="text-center">
                {buttons(item.status, setSelectedQuoteId, setField, item)}
              </div>
            </div>
          );
        })}
      </div>

      {/* No Results Found */}
      {filteredData.length === 0 && (
        <div className="text-center text-gray-500 mt-6 text-sm">
          No matching quotations found.
        </div>
      )}
    </>
  );
};

const PartQuotesDashboardScreen = ({ setScreen, part }) => {
  const [field, setField] = useState(QUOTES_DASHBOARD_SCREEN_ENUM.DASHBOARD);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);

  const getField = () => {
    switch (field) {
      case QUOTES_DASHBOARD_SCREEN_ENUM.DASHBOARD:
        return (
          <PartQuotesDashboardCode
            setScreen={setScreen}
            part={part}
            setSelectedQuoteId={setSelectedQuoteId}
            setField={setField}
          />
        );

      case QUOTES_DASHBOARD_SCREEN_ENUM.QUOTATION:
        return (
          <ViewQuotationScreen
            setField={setField}
            selectedQuoteId={selectedQuoteId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="py-8 font-roboto">
      <AnimatePresence mode="wait">
        <motion.div
          key={field}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {getField()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PartQuotesDashboardScreen;
